// One's own password, and — for a client company's primary login — the
// company's people. Everyone a client adds inherits the company's apps; the
// only per-person choice is read-only.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeOwnPassword } from '@/api/users';
import { addTeamMember, listTeam, resetTeamPassword, updateTeamMember, type TeamMember } from '@/api/team';
import { Button } from '@/components/Button';
import { useAuthStore } from '@/store/auth';

const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content';

export function Account() {
  const admin = useAuthStore((s) => s.admin);
  const isClient = useAuthStore((s) => s.isClient());
  const managesTeam = isClient && !admin?.assistant_of_admin_id && !admin?.read_only;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-content">Account</h1>
        <p className="mt-1 text-sm text-content-muted">{admin?.email}</p>
      </div>
      <PasswordForm />
      {managesTeam && <Team />}
    </div>
  );
}

function PasswordForm() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const admin = useAuthStore((s) => s.admin);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const save = useMutation({
    mutationFn: () => changeOwnPassword(current, next),
    onSuccess: (r) => {
      // Every other session ended; this one continues on the fresh token.
      if (admin) setAuth(r.token, admin);
      setCurrent(''); setNext(''); setRepeat('');
    },
  });
  const mismatch = repeat.length > 0 && next !== repeat;

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <h2 className="font-medium text-content">Change password</h2>
      <form
        className="grid gap-3 sm:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); if (!mismatch && next.length >= 8) save.mutate(); }}
      >
        <input type="password" autoComplete="current-password" placeholder="Current password"
          value={current} onChange={(e) => setCurrent(e.target.value)} className={inputClass} />
        <input type="password" autoComplete="new-password" placeholder="New password (8+ characters)"
          value={next} onChange={(e) => setNext(e.target.value)} className={inputClass} />
        <input type="password" autoComplete="new-password" placeholder="Repeat new password"
          value={repeat} onChange={(e) => setRepeat(e.target.value)} className={inputClass} />
        <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!current || next.length < 8 || mismatch}>
            Change password
          </Button>
          {mismatch && <span className="text-sm text-danger">The passwords do not match</span>}
          {save.isSuccess && <span className="text-sm text-success">Password changed. Other sessions were signed out.</span>}
          {save.error && <span className="text-sm text-danger">{(save.error as Error).message}</span>}
        </div>
      </form>
    </section>
  );
}

function Team() {
  const qc = useQueryClient();
  const teamQ = useQuery({ queryKey: ['team'], queryFn: listTeam });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });

  const add = useMutation({
    mutationFn: () => addTeamMember({ email: email.trim(), name: name.trim() || undefined, password, read_only: readOnly }),
    onSuccess: () => { setEmail(''); setName(''); setPassword(''); setReadOnly(false); void refresh(); },
  });

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <div>
        <h2 className="font-medium text-content">Team</h2>
        <p className="mt-1 text-sm text-content-subtle">
          People you add see and work on all of your company's apps. Read-only people can look but not change anything.
        </p>
      </div>

      <ul className="divide-y divide-border rounded-md border border-border">
        {(teamQ.data ?? []).map((m) => <MemberRow key={`${m.id}:${m.is_active}:${m.read_only}`} member={m} onChanged={refresh} />)}
        {teamQ.data?.length === 0 && <li className="p-3 text-sm text-content-subtle">Nobody added yet.</li>}
      </ul>

      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); if (email.trim() && password.length >= 8) add.mutate(); }}
      >
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
        <input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
        <input type="password" autoComplete="new-password" placeholder="Password (8+ characters)"
          value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />
          Read-only
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={add.isPending} disabled={!email.trim() || password.length < 8}>Add person</Button>
          {add.error && <span className="text-sm text-danger">{(add.error as Error).message}</span>}
        </div>
      </form>
    </section>
  );
}

function MemberRow({ member, onChanged }: { member: TeamMember; onChanged: () => void }) {
  const [resetting, setResetting] = useState(false);
  const [password, setPassword] = useState('');
  const update = useMutation({
    mutationFn: (input: { read_only?: boolean; is_active?: boolean }) => updateTeamMember(member.id, input),
    onSuccess: onChanged,
  });
  const reset = useMutation({
    mutationFn: () => resetTeamPassword(member.id, password),
    onSuccess: () => { setResetting(false); setPassword(''); },
  });

  return (
    <li className="p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className={`text-sm ${member.is_active ? 'text-content' : 'text-content-subtle line-through'}`}>
            {member.name ? `${member.name} · ` : ''}{member.email}
          </p>
          <p className="text-xs text-content-subtle">
            {member.is_active ? (member.read_only ? 'Read-only' : 'Can edit') : 'Switched off'}
          </p>
        </div>
        <Button variant="secondary" size="sm" disabled={update.isPending || !member.is_active}
          onClick={() => update.mutate({ read_only: !member.read_only })}>
          {member.read_only ? 'Allow editing' : 'Make read-only'}
        </Button>
        <Button variant="secondary" size="sm" disabled={update.isPending}
          onClick={() => update.mutate({ is_active: !member.is_active })}>
          {member.is_active ? 'Switch off' : 'Switch on'}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setResetting((v) => !v)}>Reset password</Button>
      </div>
      {resetting && (
        <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (password.length >= 8) reset.mutate(); }}>
          <input type="password" autoComplete="new-password" placeholder="New password (8+ characters)"
            value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputClass} max-w-xs`} />
          <Button type="submit" size="sm" loading={reset.isPending} disabled={password.length < 8}>Set</Button>
        </form>
      )}
      {(update.error || reset.error) && (
        <p className="text-sm text-danger">{((update.error || reset.error) as Error).message}</p>
      )}
      {reset.isSuccess && <p className="text-sm text-success">Password set. Their open sessions were signed out.</p>}
    </li>
  );
}
